import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { RazorPayButton } from "./RazorPay";
import { StripePaymentButton } from "./StripePayment";
import useGetInvoiceDetailForPayment from "../../../hooks/payment/useGetInvoiceDetailForPayment";
import BackButton from "../../common/BackButton";
import { handleSmartBack } from "../../../utils/navigationHelper";

/**
 * Custom Input component for payment amount
 */
const AmountInput = ({ label, showAsterisk = true, ...props }) => {
  const [field, meta, helpers] = useField(props);

  const handleChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + "." + parts[1].slice(0, 2);
    }
    helpers.setValue(value);
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-medium text-dark mb-2">
        {label} {showAsterisk && <span className="text-danger">*</span>}
      </label>
      <input
        {...field}
        {...props}
        type="text"
        inputMode="decimal"
        onChange={handleChange}
        className={`form-control form-control-lg ${meta.touched && meta.error ? "is-invalid" : ""
          }`}
        style={{ fontSize: '1.1rem' }}
      />
      {meta.touched && meta.error && (
        <div className="invalid-feedback">{meta.error}</div>
      )}
    </div>
  );
};

/**
 * Loading Skeleton
 */
const PaymentSkeleton = () => (
  <div className="container py-4">
    <div className="row justify-content-center">
      <div className="col-12 col-md-10 col-lg-8 col-xl-6">
        <div className="card shadow-sm rounded-3">
          <div className="card-header bg-primary text-white py-3">
            <div className="placeholder-glow">
              <span className="placeholder col-5" style={{ height: '24px' }}></span>
            </div>
          </div>
          <div className="card-body p-4">
            <div className="placeholder-glow mb-4">
              <span className="placeholder col-4 mb-3"></span>
              <div className="row g-3">
                <div className="col-6">
                  <span className="placeholder col-12 rounded" style={{ height: '60px' }}></span>
                </div>
                <div className="col-6">
                  <span className="placeholder col-12 rounded" style={{ height: '60px' }}></span>
                </div>
              </div>
            </div>
            <hr />
            <div className="placeholder-glow mb-4">
              <span className="placeholder col-5 mb-3"></span>
              <span className="placeholder col-12 rounded" style={{ height: '100px' }}></span>
            </div>
            <hr />
            <div className="d-grid gap-2 placeholder-glow">
              <span className="placeholder col-12 rounded" style={{ height: '50px' }}></span>
              <span className="placeholder col-12 rounded" style={{ height: '50px' }}></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Error/Warning Alert
 */
const AlertBox = ({ type, title, message, onGoBack, onRetry }) => (
  <div className="container py-4">
    <div className="row justify-content-center">
      <div className="col-12 col-md-10 col-lg-8 col-xl-6">
        <div className={`alert ${type === 'error' ? 'alert-danger' : 'alert-warning'}`}>
          <h5 className="alert-heading">{title}</h5>
          <p className="mb-3">{message}</p>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-primary" onClick={onGoBack}>
              Go Back
            </button>
            {onRetry && (
              <button className="btn btn-outline-secondary" onClick={onRetry}>
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

/**
 * LicensePayment Component
 */
export const LicensePayment = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const {
    loading,
    error,
    invoiceData: invoice,
    getInvoiceDetail,
  } = useGetInvoiceDetailForPayment();

  useEffect(() => {
    if (invoiceId) {
      getInvoiceDetail(invoiceId);
    }
  }, [invoiceId]);

  const pendingAmount =
    invoice?.due_amount !== undefined
      ? parseFloat(invoice.due_amount)
      : invoice && invoice.total_amount !== undefined && invoice.paid_amount !== undefined
        ? parseFloat(invoice.total_amount) - parseFloat(invoice.paid_amount)
        : 0;

  const validationSchema = Yup.object({
    paymentAmount: Yup.number()
      .typeError("Please enter a valid number")
      .required("Payment amount is required")
      .min(1, "Minimum payment amount is 1")
      .max(pendingAmount, `Amount cannot exceed ${pendingAmount.toFixed(2)}`)
      .test("decimal-places", "Maximum 2 decimal places allowed", (value) => {
        if (value === undefined || value === null) return true;
        const decimalParts = String(value).split(".");
        return decimalParts.length < 2 || decimalParts[1].length <= 2;
      }),
  });

  const handlePaymentSuccess = () => handleSmartBack(navigate, "/");
  const handlePaymentCancel = () => { };

  // Format currency
  const formatAmount = (amount) => {
    return parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  };

  if (loading) return <PaymentSkeleton />;

  if (error) {
    const isNotFound = error.toLowerCase().includes("not found");
    return (
      <AlertBox
        type={isNotFound ? 'warning' : 'error'}
        title={isNotFound ? 'Invoice Not Found' : 'Error Loading Invoice'}
        message={isNotFound
          ? "The invoice you're looking for doesn't exist. Please check the invoice ID."
          : "Something went wrong. Please try again."
        }
        onGoBack={() => handleSmartBack(navigate, "/")}
        onRetry={() => getInvoiceDetail(invoiceId)}
      />
    );
  }

  if (!invoice) {
    return (
      <AlertBox
        type="warning"
        title="No Invoice Data"
        message="Unable to load invoice. Please go back and try again."
        onGoBack={() => handleSmartBack(navigate, "/")}
      />
    );
  }

  if (!invoice.can_pay) {
    return (
      <AlertBox
        type="warning"
        title="Payment Not Available"
        message="Online payment is not available for this invoice."
        onGoBack={() => handleSmartBack(navigate, "/")}
      />
    );
  }

  const isPaid = pendingAmount <= 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return 'bg-success';
      case 'partial': return 'bg-warning text-dark';
      case 'pending': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const currency = invoice?.currency || "INR";

  return (
    <div className="container py-4">
      <div>
        <BackButton />
      </div>
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-6">

          <div className="card shadow-sm rounded-3 overflow-hidden">
            {/* Header - Simple blue like app theme */}
            <div className="card-header bg-primary text-white py-3">
              <h5 className="mb-0 fw-semibold">License Payment</h5>
            </div>

            <div className="card-body p-4">

              {/* Invoice Details */}
              <div className="mb-4">
                <h6 className="text-dark fw-semibold mb-3">Invoice Details</h6>
                <div className="row g-3">
                  <div className="col-12 col-sm-6">
                    <div className="bg-light rounded-2 p-3">
                      <small className="text-muted d-block mb-1">Invoice Number</small>
                      <span className="fw-medium">{invoice.invoice_no}</span>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="bg-light rounded-2 p-3">
                      <small className="text-muted d-block mb-1">Status</small>
                      <span className={`badge ${getStatusBadge(invoice.status)} text-capitalize`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              {/* Payment Summary */}
              <div className="mb-4">
                <h6 className="text-dark fw-semibold mb-3">Payment Summary</h6>
                <div className="bg-light rounded-2 p-3">
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">Total Amount</span>
                    <span className="fw-medium">{formatAmount(invoice.total_amount)} ({invoice.currency})</span>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">Paid Amount</span>
                    <span className="text-success fw-medium">{formatAmount(invoice.paid_amount)} ({invoice.currency})</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="fw-semibold">Pending Amount</span>
                    <span className="text-danger fw-bold">{formatAmount(pendingAmount)} ({invoice.currency})</span>
                  </div>
                </div>
              </div>

              {isPaid ? (
                <div className="alert alert-success mb-0">
                  <strong>Payment Complete!</strong> This invoice has been fully paid.
                </div>
              ) : (
                <Formik
                  initialValues={{ paymentAmount: pendingAmount.toFixed(2) }}
                  validationSchema={validationSchema}
                  onSubmit={() => { }}
                  enableReinitialize
                >
                  {({ values, isValid }) => (
                    <Form>
                      <hr />

                      {/* Enter Amount */}
                      <div className="mb-4">
                        <h6 className="text-dark fw-semibold mb-3">Enter Amount</h6>

                        <AmountInput
                          label={`Payment Amount (${currency})`}
                          name="paymentAmount"
                          placeholder="Enter amount"
                          showAsterisk={true}
                        />

                        <small className="text-muted">
                          You can pay any amount up to <strong>{formatAmount(pendingAmount)} ({currency})</strong>
                        </small>
                      </div>

                      <hr />

                      {/* Payment Options */}
                      <div className="mb-3">
                        <h6 className="text-dark fw-semibold mb-3">Select Payment Method</h6>

                        <div className="d-grid gap-2">
                          {currency === "INR" && (
                            <RazorPayButton
                              invoiceId={invoice.id}
                              invoice={invoice}
                              amount={values.paymentAmount}
                              onSuccess={handlePaymentSuccess}
                              onCancel={handlePaymentCancel}
                              disabled={
                                !isValid ||
                                parseFloat(values.paymentAmount) <= 0 ||
                                parseFloat(values.paymentAmount) > pendingAmount
                              }
                              currency={currency}
                            />
                          )}

                          {currency === "USD" && (
                            <StripePaymentButton
                              invoiceId={invoice.id}
                              invoice={invoice}
                              amount={values.paymentAmount}
                              onSuccess={handlePaymentSuccess}
                              onCancel={handlePaymentCancel}
                              disabled={
                                !isValid ||
                                parseFloat(values.paymentAmount) <= 0 ||
                                parseFloat(values.paymentAmount) > pendingAmount
                              }
                              currency={currency}
                            />
                          )}

                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-lg"
                            onClick={() => handleSmartBack(navigate, "/")}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>


                    </Form>
                  )}
                </Formik>
              )}
            </div>
          </div>

          {/* Help */}
          <div className="text-center mt-3">
            <small className="text-muted">
              Need help? Contact support
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicensePayment;
